<script setup>
import { ref, watch, reactive } from "vue";
import { ElMessage } from "element-plus";
import { changePasswordApi } from "@/api/sysadmin";
import { aesEncrypt } from "@/util/encrypt";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["update:visible", "confirm"]);

const dialogVisible = ref(false);
const loading = ref(false);

const formData = reactive({
  oldPassword: "",
  newPassword: "",
  confirmPassword: ""
});

watch(() => props.visible, (val) => {
  dialogVisible.value = val;
  if (val) {
    formData.oldPassword = "";
    formData.newPassword = "";
    formData.confirmPassword = "";
  }
}, { immediate: true });

watch(dialogVisible, (val) => {
  emit("update:visible", val);
});

const validateForm = () => {
  if (!formData.oldPassword.trim()) {
    ElMessage.error("请输入旧密码");
    return false;
  }
  
  if (!formData.newPassword.trim()) {
    ElMessage.error("请输入新密码");
    return false;
  }

  if (formData.newPassword.length < 8) {
    ElMessage.error("新密码至少需要8位");
    return false;
  }

  const hasLetter = /[a-zA-Z]/.test(formData.newPassword);
  const hasNumber = /\d/.test(formData.newPassword);
  if (!hasLetter || !hasNumber) {
    ElMessage.error("新密码必须包含字母和数字");
    return false;
  }

  if (formData.newPassword !== formData.confirmPassword) {
    ElMessage.error("两次输入的新密码不一致");
    return false;
  }

  return true;
};

const handleConfirm = async () => {
  if (!validateForm()) {
    return;
  }

  loading.value = true;
  
  try {
    const res = await changePasswordApi({
      oldPassword: aesEncrypt(formData.oldPassword),
      newPassword: aesEncrypt(formData.newPassword)
    });
    if (res.code === 0) {
      ElMessage.success("密码修改成功");
      emit("confirm");
      dialogVisible.value = false;
    } else {
      ElMessage.error(res.msg || "密码修改失败");
    }
  } catch (err) {
    console.error("密码修改失败", err);
    ElMessage.error("密码修改失败");
  } finally {
    loading.value = false;
  }
};

const handleCancel = () => {
  dialogVisible.value = false;
};
</script>

<template>
  <el-dialog
    v-model="dialogVisible"
    title="修改密码"
    width="6rem"
    :close-on-click-modal="false"
    destroy-on-close
  >
    <el-form :model="formData" label-width="1rem">
      <el-form-item label="旧密码">
        <el-input 
          v-model="formData.oldPassword" 
          type="password" 
          placeholder="请输入旧密码"
          show-password
        />
      </el-form-item>
      <el-form-item label="新密码">
        <el-input 
          v-model="formData.newPassword" 
          type="password" 
          placeholder="请输入新密码（至少8位，含字母和数字）"
          show-password
        />
      </el-form-item>
      <el-form-item label="确认密码">
        <el-input 
          v-model="formData.confirmPassword" 
          type="password" 
          placeholder="请再次输入新密码"
          show-password
        />
      </el-form-item>
    </el-form>
    
    <template #footer>
      <el-button :plain="true" @click="handleCancel">取消</el-button>
      <el-button type="primary" :loading="loading" @click="handleConfirm">确定修改</el-button>
    </template>
  </el-dialog>
</template>
